import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, QrCode, BarChart3, Star } from "lucide-react";

interface SourceStats {
  email: { count: number; avgRating: number; responseRate: number };
  qr: { count: number; avgRating: number };
}

interface SourceComparisonProps {
  sources: SourceStats;
  isLoading?: boolean;
}

export function SourceComparison({ sources, isLoading }: SourceComparisonProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Source Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const total = sources.email.count + sources.qr.count;
  const maxCount = Math.max(sources.email.count, sources.qr.count);
  
  const emailBarWidth = maxCount > 0 ? (sources.email.count / maxCount) * 100 : 0;
  const qrBarWidth = maxCount > 0 ? (sources.qr.count / maxCount) * 100 : 0;

  if (total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Source Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <div className="text-center">
              <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No reviews yet to compare sources.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Source Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Email Source */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <span className="font-medium">Email Requests</span>
              </div>
              <span className="text-lg font-bold">
                {sources.email.count.toLocaleString()} reviews
              </span>
            </div>
            <Progress 
              value={emailBarWidth} 
              className="h-3 [&>div]:bg-orange-500"
            />
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                Response Rate: <strong className="text-foreground">{sources.email.responseRate}%</strong>
              </span>
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <strong className="text-foreground">{sources.email.avgRating.toFixed(1)}</strong>
              </span>
            </div>
          </div>

          {/* QR Source */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <QrCode className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="font-medium">QR Code / Direct</span>
              </div>
              <span className="text-lg font-bold">
                {sources.qr.count.toLocaleString()} reviews
              </span>
            </div>
            <Progress 
              value={qrBarWidth} 
              className="h-3 [&>div]:bg-blue-500"
            />
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                Avg Rating: <strong className="text-foreground">{sources.qr.avgRating.toFixed(1)}</strong>
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
